import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, query, where, orderBy } from "firebase/firestore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Plus, Trash2, Upload } from "lucide-react";
import Stack from "@mui/material/Stack";
import { db } from "../../../lib/firebase";
import { useFirestoreCollectionOnce } from "../../../hooks/useFirestoreCollectionOnce";
import { useDeleteEventLocation } from "../../../hooks/useEventLocationMutations";
import { useSnackbar } from "../../../context/SnackbarContext";
import { PageHeader } from "../../../components/PageHeader";
import { DataTable, type Column } from "../../../components/DataTable";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { Collections, type EventLocation } from "@event-planner/shared";
import { PhoneHeaderActions } from "../../../components/phone/PhoneHeaderActions";
import type { OverflowAction } from "../../../components/phone/OverflowMenu";
import { useIsPhone } from "../../../hooks/useIsPhone";
import { ExcelLocationImportDialog } from "./ExcelLocationImportDialog";

/**
 * Locations list — every map pin in the event lives here in the new
 * reference model. Columns surface the canonical pin attributes plus a
 * coordinates display that distinguishes "label-only" entries from real
 * map pins.
 *
 * The unified Map admin tab (live preview at the top, filter chips by
 * core entity, "Used by" reverse-index column) lands in step 5/7 of the
 * locations refactor and replaces this scaffolding.
 */
export function LocationsListPage() {
  const isPhone = useIsPhone();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { remove, loading: deleting } = useDeleteEventLocation();
  const [deleteTarget, setDeleteTarget] = useState<EventLocation | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const locationsQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, Collections.EVENT_LOCATIONS),
            where("eventId", "==", eventId),
            orderBy("label", "asc"),
          )
        : null,
    [eventId],
  );

  const {
    data: locations,
    loading,
    error,
    refetch,
  } = useFirestoreCollectionOnce<EventLocation>(locationsQuery);

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.id);
      showSuccess("Location deleted");
      setDeleteTarget(null);
      refetch();
    } catch {
      showError("Failed to delete location");
    }
  }

  const columns: Column<EventLocation>[] = [
    {
      id: "color",
      label: "",
      width: "32px",
      render: (l) => (
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            backgroundColor: l.color,
            display: "inline-block",
          }}
          aria-label={`Pin color ${l.color}`}
        />
      ),
    },
    { id: "label", label: "Label", render: (l) => l.label },
    {
      id: "address",
      label: "Address",
      render: (l) =>
        // Compose address line preferring street + city, falling back
        // to whatever parts are present. Many label-only locations have
        // no address at all and render as "—".
        [l.address, l.city, l.stateOrRegion]
          .filter(Boolean)
          .join(", ") || "—",
    },
    {
      id: "coordinates",
      label: "Map Pin",
      render: (l) =>
        l.coordinates
          ? `${l.coordinates.lat.toFixed(4)}, ${l.coordinates.lng.toFixed(4)}`
          : "label only",
    },
    {
      id: "actions",
      label: "",
      align: "right",
      render: (l) => (
        <Tooltip title="Delete location">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(l);
            }}
            aria-label={`Delete ${l.label}`}
          >
            <Trash2 size={18} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Phone: the pin-color dot sits beside the label on the title line,
  // the address and pin follow in words, and delete moves to the row menu.
  const phoneColumns: Column<EventLocation>[] = [
    {
      id: "label",
      label: "Label",
      phone: "primary",
      render: (l) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: l.color,
              flexShrink: 0,
            }}
            aria-label={`Pin color ${l.color}`}
          />
          <span>{l.label}</span>
        </Stack>
      ),
    },
    { ...columns.find((c) => c.id === "address")!, phone: "secondary" },
    {
      id: "coordinates",
      label: "Map Pin",
      phone: "secondary",
      render: (l) =>
        l.coordinates
          ? `Pin ${l.coordinates.lat.toFixed(4)}, ${l.coordinates.lng.toFixed(4)}`
          : "No map pin (label only)",
    },
  ];
  function locationRowActions(l: EventLocation): OverflowAction[] {
    return [
      {
        label: "Delete location",
        icon: <Trash2 size={18} />,
        onClick: () => setDeleteTarget(l),
        destructive: true,
      },
    ];
  }

  return (
    <Box data-testid="locations-page">
      <PageHeader
        title="Locations"
        action={
          isPhone ? (
            <PhoneHeaderActions
              primary={
                <Button
                  variant="contained"
                  startIcon={<Plus size={18} />}
                  onClick={() => navigate("new")}
                  data-testid="new-location-button"
                >
                  New Location
                </Button>
              }
              more={[
                {
                  label: "Import from Excel",
                  icon: <Upload size={16} />,
                  onClick: () => setImportOpen(true),
                  testId: "import-locations-button",
                },
              ]}
            />
          ) : (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Upload size={16} />}
              onClick={() => setImportOpen(true)}
              data-testid="import-locations-button"
            >
              Import from Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => navigate("new")}
              data-testid="new-location-button"
            >
              New Location
            </Button>
          </Stack>
          )
        }
      />
      {!loading && !error && locations.length === 0 ? (
        <Typography color="text.secondary">
          No locations yet. Locations are the canonical map pins for this
          event. Sessions, groups, the venue, and travel items reference
          them by id; pins on the mobile map come from this list.
        </Typography>
      ) : (
        <DataTable
          columns={isPhone ? phoneColumns : columns}
          data={locations}
          loading={loading}
          error={error}
          ariaLabel="Locations table"
          getRowKey={(l) => l.id}
          onRowClick={(l) => navigate(l.id)}
          phoneRowActions={locationRowActions}
          phoneRowActionsLabel={(l) => `More actions for ${l.label}`}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Location"
        message={`Delete "${deleteTarget?.label}"? This cannot be undone.`}
        loading={deleting}
      />

      {eventId && (
        <ExcelLocationImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          eventId={eventId}
          onImported={refetch}
        />
      )}
    </Box>
  );
}
